import { useEffect, useMemo, useState } from "react";
import { dayLabel, formatTrDate, parseIsoDate } from "../../utils/dates";
import { NewAppointmentSlotContext } from "./NewAppointmentModal";
import "./WeeklyCalendar.css";

export type Reservation = {
  id: string;
  starts_at: string;
  ends_at: string;
  lesson_type: "normal" | "special" | "extra" | string;
  status: string;
  conflict_accepted: boolean;
  student_name?: string | null;
  vehicle_summary?: string | null;
  vehicle_gear_type?: string | null;
  report_attended?: boolean | null;
  student_id?: string;
  instructor_id?: string;
  vehicle_id?: string;
};

export type AppointmentEditContext = {
  appointmentId: string;
  date: string;
  startHHmm: string;
  endHHmm: string;
  studentId: string;
  instructorId: string;
  vehicleId: string;
  lessonType: "normal" | "special" | "extra";
  preferredInstructorId?: string;
};

export type Slot = {
  index: number;
  start: string;
  end: string;
  status: "available" | "occupied";
  reservation_count: number;
  reservations: Reservation[];
};

export type DayBlock = {
  date: string;
  slots: Slot[];
};

export type WeekResponse = {
  start_date: string;
  end_date: string;
  branch_id?: string | null;
  instructor_id?: string | null;
  instructor_ids?: string[] | null;
  vehicle_id?: string | null;
  settings?: unknown;
  days: DayBlock[];
};

type SelectedSlot = {
  date: string;
  slot: Slot;
};

function isSameLocalDate(iso: string, today: Date): boolean {
  const target = parseIsoDate(iso);
  return (
    target.getFullYear() === today.getFullYear() &&
    target.getMonth() === today.getMonth() &&
    target.getDate() === today.getDate()
  );
}

const GEAR_PAREN_SUFFIX_RE = /\s*\((Manuel|Otomatik)\)\s*$/i;
const GEAR_PREFIX_RE = /^(Manuel|Otomatik)\s*([·|]\s*|-\s*)?/i;
const LEGACY_LESSON_GEAR_PREFIX_RE =
  /^(Normal|Ozel|Özel|Ekstra|Ek)(\s+Ders)?\s*[-·|]\s*(Manuel|Otomatik)\s*[-·|]\s*/i;

/** Model metninden parantez icindeki vitesi temizler (ayri alanda gosterilir). */
export function formatVehicleSummaryForDisplay(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  let s = raw.trim();
  const legacyPipe = s.match(/^[^|]+\|\s*(.+)$/);
  if (legacyPipe) {
    s = legacyPipe[1].trim();
  }
  s = s.replace(LEGACY_LESSON_GEAR_PREFIX_RE, "");
  s = s.replace(GEAR_PREFIX_RE, "");
  const segments = s
    .split(/\s*·\s*|\s*-\s*/)
    .map((seg) => seg.replace(GEAR_PAREN_SUFFIX_RE, "").trim())
    .filter(Boolean);
  return segments.length > 0 ? segments.join(" - ") : null;
}

export function gearTypeLabel(gearType?: string | null): string {
  if (gearType === "manual") return "Manuel";
  if (gearType === "automatic") return "Otomatik";
  return "";
}

/** Randevu kutusu: plaka - model - Manuel/Otomatik (vites tek Satırda). */
export function formatVehicleLine(
  summary: string | null | undefined,
  gearType?: string | null,
): string | null {
  const base = formatVehicleSummaryForDisplay(summary);
  const gear = gearTypeLabel(gearType);
  const parts = base ? base.split(/\s*-\s*/).filter(Boolean) : [];
  if (gear) parts.push(gear);
  return parts.length > 0 ? parts.join(" - ") : null;
}

export function formatVehicleOptionLabel(
  plate: string,
  brandModel: string | null | undefined,
  gearType?: string | null,
): string {
  return formatVehicleLine(
    [plate, brandModel ? brandModel.replace(GEAR_PAREN_SUFFIX_RE, "").trim() : ""].filter(Boolean).join(" · "),
    gearType,
  ) || plate;
}

export function lessonTypeShort(lessonType: string): string {
  switch (lessonType) {
    case "normal":
      return "Normal Ders";
    case "special":
      return "Özel Ders";
    case "extra":
      return "Ek Ders";
    default:
      return lessonType;
  }
}

/** Ders bitmiş, rapor girilmemiş (değerlendirme bekliyor). */
export function isEvaluationPending(
  reservation: Pick<Reservation, "status" | "ends_at">,
  nowMs: number = Date.now(),
): boolean {
  if (reservation.status === "completed" || reservation.status === "cancelled") {
    return false;
  }
  const endMs = new Date(reservation.ends_at).getTime();
  return Number.isFinite(endMs) && endMs < nowMs;
}

export function reservationChipLines(reservation: Reservation): { primary: string; secondary: string | null } {
  const name = reservation.student_name?.trim();
  const vehicle = formatVehicleLine(reservation.vehicle_summary, reservation.vehicle_gear_type);
  if (name) {
    return { primary: name, secondary: vehicle };
  }
  if (vehicle) {
    return { primary: lessonTypeShort(reservation.lesson_type), secondary: vehicle };
  }
  return { primary: lessonTypeShort(reservation.lesson_type), secondary: null };
}

export function lessonClass(lessonType: string): string {
  switch (lessonType) {
    case "normal":
      return "kp-cal-cell__chip--normal";
    case "special":
      return "kp-cal-cell__chip--special";
    case "extra":
      return "kp-cal-cell__chip--extra";
    default:
      return "kp-cal-cell__chip--normal";
  }
}

export function dailyLessonClass(lessonType: string): string {
  switch (lessonType) {
    case "normal":
      return "kp-daily-cal__busy--normal";
    case "special":
      return "kp-daily-cal__busy--special";
    case "extra":
      return "kp-daily-cal__busy--extra";
    default:
      return "kp-daily-cal__busy--normal";
  }
}

export function buildAppointmentEditContext(
  reservation: Reservation,
  date: string,
  startHHmm: string,
  endHHmm: string,
): AppointmentEditContext | null {
  if (!reservation.student_id || !reservation.instructor_id || !reservation.vehicle_id) {
    return null;
  }
  const lt = reservation.lesson_type;
  if (lt !== "normal" && lt !== "special" && lt !== "extra") {
    return null;
  }
  return {
    appointmentId: reservation.id,
    date,
    startHHmm,
    endHHmm,
    studentId: reservation.student_id,
    instructorId: reservation.instructor_id,
    vehicleId: reservation.vehicle_id,
    lessonType: lt,
    preferredInstructorId: reservation.instructor_id,
  };
}

export type AppointmentDropTarget = {
  appointmentId: string;
  source: { date: string; startHHmm: string; endHHmm: string };
  destination: { date: string; startHHmm: string; endHHmm: string };
};

type WeeklyCalendarProps = {
  data: WeekResponse;
  onCreateAppointment?: (ctx: NewAppointmentSlotContext) => void;
  /** Secili slotu ust toolbar'a bildirir (+ Yeni Randevu disarida acilir). */
  onToolbarCreateSlotChange?: (ctx: NewAppointmentSlotContext | null) => void;
  /** true ise panel icindeki + Yeni Randevu gizlenir (toolbar kullanilir). */
  hideInlineCreate?: boolean;
  onMoveAppointment?: (target: AppointmentDropTarget) => void;
  onShowAuditHistory?: (appointmentId: string) => void;
  onOpenLessonReport?: (appointment: {
    id: string;
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
  }) => void;
};

type DragInfo = {
  appointmentId: string;
  date: string;
  startHHmm: string;
  endHHmm: string;
};

export function WeeklyCalendar({
  data,
  onCreateAppointment,
  onToolbarCreateSlotChange,
  hideInlineCreate,
  onMoveAppointment,
  onShowAuditHistory,
  onOpenLessonReport,
}: WeeklyCalendarProps) {
  const [selected, setSelected] = useState<SelectedSlot | null>(null);
  const [drag, setDrag] = useState<DragInfo | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    if (!onToolbarCreateSlotChange) return;
    if (!selected || !onCreateAppointment) {
      onToolbarCreateSlotChange(null);
      return;
    }
    onToolbarCreateSlotChange({
      date: selected.date,
      startHHmm: selected.slot.start,
      endHHmm: selected.slot.end,
    });
  }, [selected, onCreateAppointment, onToolbarCreateSlotChange]);

  const slotIndices = useMemo(() => {
    const seen = new Set<number>();
    const ordered: number[] = [];
    data.days.forEach((day) => {
      day.slots.forEach((slot) => {
        if (!seen.has(slot.index)) {
          seen.add(slot.index);
          ordered.push(slot.index);
        }
      });
    });
    return ordered.sort((a, b) => a - b);
  }, [data]);

  const slotByDay = useMemo(() => {
    const map = new Map<string, Map<number, Slot>>();
    data.days.forEach((day) => {
      const inner = new Map<number, Slot>();
      day.slots.forEach((slot) => inner.set(slot.index, slot));
      map.set(day.date, inner);
    });
    return map;
  }, [data]);

  const slotTimes = useMemo(() => {
    const map = new Map<number, { start: string; end: string }>();
    data.days.forEach((day) => {
      day.slots.forEach((slot) => {
        if (!map.has(slot.index)) {
          map.set(slot.index, { start: slot.start, end: slot.end });
        }
      });
    });
    return map;
  }, [data]);

  return (
    <div className="kp-cal">
      <div
        className="kp-cal__grid"
        style={{ gridTemplateColumns: `90px repeat(${data.days.length}, minmax(120px, 1fr))` }}
      >
        <div className="kp-cal__corner" />
        {data.days.map((day, idx) => {
          const dateObj = parseIsoDate(day.date);
          const isToday = isSameLocalDate(day.date, today);
          return (
            <div
              key={day.date}
              className={`kp-cal__day-head${isToday ? " kp-cal__day-head--today" : ""}`}
            >
              <span className="kp-cal__day-name">{dayLabel(idx, true)}</span>
              <span className="kp-cal__day-date">
                {dateObj.getDate()} {formatTrDate(day.date).split(" ")[1]}
              </span>
            </div>
          );
        })}

        {slotIndices.map((slotIndex) => {
          const time = slotTimes.get(slotIndex);
          return (
            <div className="kp-cal__row" key={`row-${slotIndex}`}>
              <div className="kp-cal__time">
                <strong>{time?.start || ""}</strong>
                <span>{time?.end || ""}</span>
              </div>
              {data.days.map((day) => {
                const slot = slotByDay.get(day.date)?.get(slotIndex);
                if (!slot) {
                  return <div className="kp-cal__cell kp-cal__cell--gap" key={`${day.date}-${slotIndex}`} />;
                }
                const isSelected =
                  selected && selected.date === day.date && selected.slot.index === slot.index;
                const occupied = slot.status === "occupied";
                const cellKey = `${day.date}-${slotIndex}`;
                const isDragOver = dragOverKey === cellKey;

                const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
                  if (!drag || !onMoveAppointment) return;
                  if (drag.date === day.date && drag.startHHmm === slot.start && drag.endHHmm === slot.end) {
                    return;
                  }
                  event.preventDefault();
                  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                  if (dragOverKey !== cellKey) setDragOverKey(cellKey);
                };

                const handleDragLeave = () => {
                  if (dragOverKey === cellKey) setDragOverKey(null);
                };

                const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  setDragOverKey(null);
                  if (!drag || !onMoveAppointment) return;
                  if (drag.date === day.date && drag.startHHmm === slot.start && drag.endHHmm === slot.end) {
                    setDrag(null);
                    return;
                  }
                  const target: AppointmentDropTarget = {
                    appointmentId: drag.appointmentId,
                    source: { date: drag.date, startHHmm: drag.startHHmm, endHHmm: drag.endHHmm },
                    destination: { date: day.date, startHHmm: slot.start, endHHmm: slot.end },
                  };
                  setDrag(null);
                  onMoveAppointment(target);
                };

                return (
                  <button
                    key={cellKey}
                    type="button"
                    onClick={() => setSelected({ date: day.date, slot })}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={[
                      "kp-cal__cell",
                      occupied ? "kp-cal__cell--occupied" : "kp-cal__cell--available",
                      isSelected ? "kp-cal__cell--selected" : "",
                      isDragOver ? "kp-cal__cell--drag-over" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-label={`${day.date} ${slot.start}-${slot.end}`}
                  >
                    {slot.reservations.length === 0 ? (
                      <span className="kp-cal__cell-empty">Bos</span>
                    ) : (
                      slot.reservations.map((reservation) => {
                        const isDraggable = Boolean(onMoveAppointment);
                        const { primary, secondary } = reservationChipLines(reservation);
                        const titleText = `${primary}${secondary ? ` — ${secondary}` : ""}${
                          reservation.conflict_accepted ? " (istisna)" : ""
                        }${isDraggable ? " · Surukleyerek tasiyabilirsiniz" : ""}`;
                        return (
                          <span
                            key={reservation.id}
                            className={`kp-cal-cell__chip kp-cal-cell__chip--block ${lessonClass(reservation.lesson_type)}${
                              reservation.conflict_accepted ? " kp-cal-cell__chip--conflict" : ""
                            }${isDraggable ? " kp-cal-cell__chip--draggable" : ""}`}
                            title={titleText}
                            draggable={isDraggable}
                            onDragStart={(event) => {
                              if (!isDraggable) return;
                              event.stopPropagation();
                              if (event.dataTransfer) {
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", reservation.id);
                              }
                              setDrag({
                                appointmentId: reservation.id,
                                date: day.date,
                                startHHmm: slot.start,
                                endHHmm: slot.end,
                              });
                            }}
                            onDragEnd={() => {
                              setDrag(null);
                              setDragOverKey(null);
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelected({ date: day.date, slot });
                            }}
                          >
                            <span className="kp-cal-cell__chip__row">
                              <span className="kp-cal-cell__chip__name">{primary}</span>
                              {reservation.conflict_accepted ? (
                                <span className="kp-cal-cell__chip__flag" title="Cakisma istisnasi">
                                  !
                                </span>
                              ) : null}
                            </span>
                            {secondary ? (
                              <span className="kp-cal-cell__chip__sub">{secondary}</span>
                            ) : null}
                          </span>
                        );
                      })
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {selected && (
        <SlotDetailPanel
          date={selected.date}
          slot={selected.slot}
          onClose={() => setSelected(null)}
          hideInlineCreate={hideInlineCreate}
          onCreate={
            onCreateAppointment && !hideInlineCreate
              ? () =>
                  onCreateAppointment({
                    date: selected.date,
                    startHHmm: selected.slot.start,
                    endHHmm: selected.slot.end,
                  })
              : undefined
          }
          onShowAuditHistory={onShowAuditHistory}
          onOpenLessonReport={onOpenLessonReport}
        />
      )}
    </div>
  );
}

function SlotDetailPanel({
  date,
  slot,
  onClose,
  onCreate,
  hideInlineCreate,
  onShowAuditHistory,
  onOpenLessonReport,
}: {
  date: string;
  slot: Slot;
  onClose: () => void;
  onCreate?: () => void;
  hideInlineCreate?: boolean;
  onShowAuditHistory?: (appointmentId: string) => void;
  onOpenLessonReport?: (appointment: {
    id: string;
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
  }) => void;
}) {
  return (
    <div className="kp-cal-detail kp-card">
      <div className="kp-cal-detail__head">
        <div>
          <strong>{formatTrDate(date)}</strong>
          <span>
            {slot.start} - {slot.end}
          </span>
        </div>
        <div className="kp-cal-detail__head-actions">
          {onCreate && !hideInlineCreate && (
            <button className="kp-btn kp-btn--primary" onClick={onCreate}>
              + Yeni Randevu
            </button>
          )}
          <button className="kp-btn kp-btn--ghost" onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
      {slot.reservations.length === 0 && !hideInlineCreate && (
        <p className="kp-cal-detail__empty">Bu ders saati bos. Buraya yeni randevu olusturulabilir.</p>
      )}
      {slot.reservations.length === 0 && hideInlineCreate && (
        <p className="kp-cal-detail__toolbar-hint">
          Bos ders saati. Yeni randevu icin sayfanin <strong>sag ustundeki + Yeni Randevu</strong> dugmesine basin.
        </p>
      )}
      {slot.reservations.length > 0 && (
        <ul className="kp-cal-detail__list">
          {slot.reservations.map((reservation) => {
            const vehicleLine = formatVehicleLine(
              reservation.vehicle_summary,
              reservation.vehicle_gear_type,
            );
            return (
            <li key={reservation.id} className="kp-cal-detail__item">
              <div>
                <strong>
                  {reservation.student_name?.trim() || lessonTypeShort(reservation.lesson_type)}
                </strong>
                {vehicleLine ? (
                  <span className="kp-cal-detail__vehicle">{vehicleLine}</span>
                ) : null}
                <span className="kp-cal-detail__meta">
                  {new Date(reservation.starts_at).toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "Europe/Istanbul",
                  })}
                  {" - "}
                  {new Date(reservation.ends_at).toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "Europe/Istanbul",
                  })}
                  {" · "}
                  {lessonTypeShort(reservation.lesson_type)}
                </span>
              </div>
              <div className="kp-cal-detail__tags">
                <span className={`kp-cal-detail__tag kp-cal-detail__tag--${reservation.status}`}>
                  {reservation.status}
                </span>
                {reservation.conflict_accepted && (
                  <span className="kp-cal-detail__tag kp-cal-detail__tag--conflict">
                    cakisma istisnasi
                  </span>
                )}
                {onShowAuditHistory && (
                  <button
                    type="button"
                    className="kp-btn kp-btn--ghost kp-cal-detail__history-btn"
                    onClick={() => onShowAuditHistory(reservation.id)}
                  >
                    Gecmisi Gor
                  </button>
                )}
                {onOpenLessonReport && reservation.status !== "cancelled" && (
                  <button
                    type="button"
                    className="kp-btn kp-btn--primary kp-cal-detail__history-btn"
                    onClick={() =>
                      onOpenLessonReport({
                        id: reservation.id,
                        starts_at: reservation.starts_at,
                        ends_at: reservation.ends_at,
                        lesson_type: reservation.lesson_type,
                        status: reservation.status,
                      })
                    }
                  >
                    Ders Raporu
                  </button>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
